/**
 * SOFT_DELETE_STANDARD (F4-A) — papelera local-first por tipo de entidad.
 *
 * Registro curado de los tipos con papelera en la UI (las tablas soft-delete
 * sincronizadas con un flujo de borrado real en la app). Cada entrada sabe
 * consultar sus filas borradas y extraer una etiqueta humana. Restaurar/purgar
 * viven en `./mutations` y pasan por el write-path normal de PowerSync.
 */
import {
  announcement,
  cohort,
  course_module,
  desc,
  forum_thread,
  isNotNull,
  type PowerSyncDrizzleDB,
  program,
  resource,
  schedule_slot,
} from "@space/powersync";

export const TRASH_KINDS = [
  "cohort",
  "course_module",
  "announcement",
  "forum_thread",
  "schedule_slot",
  "program",
  "resource",
] as const;

export type TrashKind = (typeof TRASH_KINDS)[number];

/** Fila de papelera normalizada para la UI. */
export interface TrashedItem {
  kind: TrashKind;
  id: string;
  /** Etiqueta humana (título/nombre); null si la entidad no tiene. */
  label: string | null;
  /** ISO timestamp del borrado (para "borrado hace X" y la cuenta atrás de purga). */
  deletedAt: string | null;
}

export const trashQueryKeys = {
  all: ["trash"] as const,
  byKind: (userId: string | undefined, kind: TrashKind) => ["trash", userId, kind] as const,
  byKindCount: (userId: string | undefined, kind: TrashKind) =>
    ["trash", userId, kind, "count"] as const,
};

/** Tabla drizzle de cada kind (para restaurar/purgar genérico). */
export const TRASH_TABLES = {
  cohort,
  course_module,
  announcement,
  forum_thread,
  schedule_slot,
  program,
  resource,
} as const;

/** Query de filas borradas de un kind (más recientes primero). */
export function getTrashedQuery(db: PowerSyncDrizzleDB, kind: TrashKind) {
  const table = TRASH_TABLES[kind];
  return db.select().from(table).where(isNotNull(table.deleted_at)).orderBy(desc(table.deleted_at));
}

/** Etiqueta humana de una fila cruda de papelera, por kind. */
export function trashedLabel(kind: TrashKind, row: Record<string, unknown>): string | null {
  const str = (key: string): string | null => {
    const v = row[key];
    return typeof v === "string" && v.length > 0 ? v : null;
  };
  switch (kind) {
    case "cohort":
    case "program": {
      return str("name");
    }
    case "announcement":
    case "forum_thread":
    case "resource": {
      return str("title");
    }
    case "course_module": {
      return str("kind");
    }
    case "schedule_slot": {
      const start = str("start_time");
      const end = str("end_time");
      return start && end ? `${start}–${end}` : start;
    }
    default: {
      return null;
    }
  }
}

/** Días restantes hasta la purga física (server: TRASH_PURGE_DAYS, default 30). */
export const DEFAULT_TRASH_PURGE_DAYS = 30;

export function daysUntilPurge(
  deletedAt: string | null,
  purgeDays: number = DEFAULT_TRASH_PURGE_DAYS,
  now: number = Date.now(),
): number | null {
  if (!deletedAt) {
    return null;
  }
  const deleted = Date.parse(deletedAt);
  if (Number.isNaN(deleted)) {
    return null;
  }
  const msLeft = deleted + purgeDays * 24 * 60 * 60 * 1000 - now;
  return Math.max(0, Math.ceil(msLeft / (24 * 60 * 60 * 1000)));
}
